import {createStyles, Switch} from '@mantine/core';
import {css, StyleSheet} from 'aphrodite';
import React, {useCallback, useState} from 'react';
import isEqual from 'react-fast-compare';

import * as analytics from 'analytics/Analytics';
import SettingsModal from 'settings/visibility-options/SettingsModal';
import {
  ANNOTATIONS,
  EXPAND_MINI_MAP,
  FeatureCollectionName,
  FeatureCounts,
  MAXIMISE,
  PART_ANNOTATIONS,
  PRIMERS,
  TRANSLATIONS,
  TRANSCRIPTIONS,
  VisibilityOption,
  VisibilityOptions,
  VisibilityOptionSettings,
} from 'settings/visibility-options/VisibilityOptions';
import {
  FONT_PROXIMA_11,
  FONT_PROXIMA_13,
  FONT_WEIGHT_PROXIMA_REGULAR,
  FONT_WEIGHT_PROXIMA_SEMIBOLD,
} from 'styles/FontConstants';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {ORANGE_LIGHT, SILVER, GRAY_LIGHT} from 'ui-library/Colors';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import useEventHandler from 'ui-library/hooks/useEventHandler';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import legacyRunModal from 'ui-library/legacyRunModal';
import Menu from 'ui-library/menus/Menu';
import {MenuHeight, MenuWidth} from 'ui-library/menus/MenuConstants';
import Option from 'ui-library/menus/Option';
import {Optional} from 'util/Types';

const useStyles = createStyles(() => ({
  switchRoot: {
    cursor: 'pointer',
  },
  switchBody: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
    padding: '8px 16px',
  },
  switchLabel: {
    display: 'flex',
    alignItems: 'center',
    columnGap: '4px',
    fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
    ...FONT_PROXIMA_13,
  },
}));

export interface DisplayOptionsProps {
  children: JSX.Element;
  options: VisibilityOptions;
  featureCounts?: FeatureCounts;
  dropLeft: boolean;
}

// Map from visibility option name to feature count name.
// TODO: We should just rename the feature count names and remove this constant.
const VISIBILITY_OPTION_NAME_TO_FEATURE_COUNT_NAME: {[visibilityOptionName: string]: FeatureCollectionName} =
  {
    [ANNOTATIONS.name]: 'annotations',
    [PRIMERS.name]: 'sequencePrimers',
    [PART_ANNOTATIONS.name]: 'parts',
    [TRANSLATIONS.name]: 'translations',
    [TRANSCRIPTIONS.name]: 'transcriptions',
  };

interface VisibilityOptionDatum {
  cssWarning: Optional<string>;
  descriptionComponent: Optional<React.FC>;
  disabled: boolean;
  name: string;
  displayName: string;
  indeterminate: boolean;
  setOptionVisibility: (visible: boolean) => void;
  // TODO: This attribute uses a Backbone model. We should change it to use only plain objects.
  settings: Optional<VisibilityOptionSettings>;
  visible: boolean;
}

// TODO: Remove conversion once we can pass in plain objects to this component, instead of Backbone models.
function getOptionDatumFromOptionBackboneModel(option: VisibilityOption): VisibilityOptionDatum {
  return {
    name: option.get('name'),
    cssWarning: option.get('cssWarning'),
    descriptionComponent: option.get('descriptionComponent'),
    disabled: option.get('disabled'),
    displayName: option.get('displayName'),
    indeterminate: option.get('indeterminate'),
    setOptionVisibility: (visible: boolean) => {
      option.set('visible', visible);
    },
    settings: option.get('settings'),
    visible: option.get('visible'),
  };
}

export function DisplayOptions({
  options,
  featureCounts,
  dropLeft,
  children,
}: DisplayOptionsProps): JSX.Element {
  const {classes} = useStyles();
  const maybeMaximizeOption = options.getOption(MAXIMISE.name);
  const isMaximized = maybeMaximizeOption != null ? maybeMaximizeOption.get('visible') : true;
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const [optionData, setOptionData] = useState<Array<VisibilityOptionDatum>>(() =>
    options.getEditableOptions().map((option) => getOptionDatumFromOptionBackboneModel(option))
  );

  const handleOptionUpdate = useCallback((option: VisibilityOption) => {
    setOptionData(
      (option.collection as VisibilityOptions)
        .getEditableOptions()
        .map((option) => getOptionDatumFromOptionBackboneModel(option))
    );
  }, []);

  useEventHandler(options, 'change', handleOptionUpdate);

  return (
    <Dropdown
      closeOnInsideClick={false}
      isOpen={isDropdownOpen}
      onToggle={setIsDropdownOpen}
      menu={
        <Menu
          dropLeft={dropLeft}
          // Avoid default styling of selected options.
          value={[]}
          isMulti={true}
          scrollable={true}
          height={MenuHeight.MEDIUM}
          width={MenuWidth.LARGE}
        >
          {optionData.map((optionDatum) => {
            const disabled =
              optionDatum.name === MAXIMISE.name ? false : optionDatum.disabled || !isMaximized;
            const optionName = optionDatum.name;
            const optionFeatureCountName = VISIBILITY_OPTION_NAME_TO_FEATURE_COUNT_NAME[optionName];
            const featureCount =
              optionFeatureCountName != null ? featureCounts?.[optionFeatureCountName] : undefined;
            const onSelect = (newValue: boolean): void => {
              optionDatum.setOptionVisibility(newValue);
              if (optionDatum.name === EXPAND_MINI_MAP.name) {
                // This visibility option causes the entire view to re-render, which causes the dropdown
                // to be misplaced. Manually close the dropdown to prevent.
                // TODO: Remove when the alignment view is rendered using React instead of Backbone.
                setIsDropdownOpen(false);
              }
              analytics.track('editor', 'toggled_visibility', {
                option: optionDatum.name,
                visible: newValue,
              });
            };

            return (
              <Option
                dataTestAttrs={{
                  component: 'DisplayOptions',
                  element: 'visibilityOption',
                  key: optionDatum.name,
                }}
                contentClassName={css(styles.noPadding)}
                childrenClassName={css(styles.noPadding)}
                skipIconOverride={true}
                isDisabled={disabled}
                key={optionName}
                value={optionName}
              >
                <Switch
                  checked={optionDatum.visible}
                  classNames={{
                    root: classes.switchRoot,
                    body: classes.switchBody,
                    label: classes.switchLabel,
                  }}
                  labelPosition="left"
                  label={
                    <>
                      <span className={css(styles.centeredContent)}>{optionDatum.displayName}</span>
                      {optionDatum.cssWarning && (
                        <div className={css(styles.centeredContent)}>
                          <Tooltip text={optionDatum.cssWarning}>
                            <BenchlingIcon style={{color: ORANGE_LIGHT}} icon="warning" />
                          </Tooltip>
                        </div>
                      )}
                      {featureCount != null && <div className={css(styles.badge)}>{featureCount}</div>}
                      {optionDatum.settings != null && optionDatum.settings.length > 0 && (
                        <span
                          className={css(styles.centeredContent)}
                          onClick={(e) => {
                            if (disabled) {
                              return;
                            }
                            // Manually close the dropdown since we're about to open a modal and we want to
                            // avoid z-index issues.
                            setIsDropdownOpen(false);
                            e.stopPropagation();
                            e.preventDefault();
                            legacyRunModal(SettingsModal, {
                              settings: optionDatum.settings!,
                              Description: optionDatum.descriptionComponent!,
                            });
                          }}
                        >
                          <BenchlingIcon icon="gear" style={{color: SILVER}} />
                        </span>
                      )}
                    </>
                  }
                  onChange={() => {
                    onSelect(!optionDatum.visible);
                  }}
                  disabled={disabled}
                  size="sm"
                  // NOTE: this component does not support indeterminate state, so we do not explicitly handle it.
                />
              </Option>
            );
          })}
        </Menu>
      }
    >
      {children}
    </Dropdown>
  );
}

function areEqual(props: DisplayOptionsProps, nextProps: DisplayOptionsProps): boolean {
  return (
    isEqual(props.options, nextProps.options) &&
    props.featureCounts === nextProps.featureCounts &&
    props.dropLeft === nextProps.dropLeft
  );
}

const MemoizedDisplayOptions = React.memo(DisplayOptions, areEqual);

export function AlignmentRowVisibilityOptionsDropdown(
  props: Omit<DisplayOptionsProps, 'children'>
): JSX.Element {
  return (
    <MemoizedDisplayOptions {...props}>
      <div className="button-wrapper alignment-button-wrapper">
        <Button buttonStyle={ButtonStyle.TERTIARY_WHITE} size={ButtonSize.MINI} isDropdown={true} />
        <ul className="dropdown-menu select" />
      </div>
    </MemoizedDisplayOptions>
  );
}

export default function DefaultDisplayOptionsDropdown(
  props: Omit<DisplayOptionsProps, 'children'>
): JSX.Element {
  return (
    <MemoizedDisplayOptions {...props}>
      <Button
        buttonStyle={ButtonStyle.TERTIARY_GRAY}
        dataTestAttrs={{component: 'DisplayOptions', element: 'visibilityOptionsDropdownToggle'}}
        icon="settings"
        isDropdown={true}
        size={ButtonSize.MINI}
      />
    </MemoizedDisplayOptions>
  );
}

const styles = StyleSheet.create({
  noPadding: {
    padding: 0,
  },
  centeredContent: {
    alignContent: 'center',
  },
  badge: {
    padding: '0 4px',
    borderRadius: '25px',
    backgroundColor: GRAY_LIGHT,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    ...FONT_PROXIMA_11,
  },
});
